/**
 * Custom hook for authenticated API requests
 * Uses @hiyve/react-identity's useAuthClient for token management
 */

import { useCallback } from 'react';
import { useAuthClient } from '@hiyve/react-identity';

/**
 * Build URL with query parameters
 */
function buildUrl(endpoint, params = {}) {
  const url = new URL(endpoint, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, value);
    }
  });
  return url.pathname + url.search;
}

/**
 * Hook that provides authenticated API methods for use with TanStack Query
 *
 * Usage:
 * ```javascript
 * const api = useApi();
 *
 * // With TanStack Query
 * const { data } = useQuery({
 *   queryKey: ['rooms'],
 *   queryFn: () => api.get('/api/rooms'),
 * });
 *
 * // Direct usage
 * const result = await api.post('/api/rooms', { name: 'New Room' });
 * ```
 */
export function useApi() {
  const authClient = useAuthClient();

  /**
   * Make an authenticated fetch request
   */
  const authFetch = useCallback(async (url, options = {}) => {
    const token = await authClient.getAccessToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!response.ok) {
      let error;
      if (isJson) {
        const errorData = await response.json();
        error = new Error(errorData.message || errorData.error || 'Request failed');
        error.code = errorData.code;
        error.status = response.status;
      } else {
        error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
      }
      throw error;
    }

    if (isJson) {
      return response.json();
    }
    return response.text();
  }, [authClient]);

  /**
   * GET request
   */
  const get = useCallback(async (endpoint, params = {}) => {
    const url = buildUrl(endpoint, params);
    return authFetch(url, { method: 'GET' });
  }, [authFetch]);

  /**
   * POST request
   */
  const post = useCallback(async (endpoint, body = {}) => {
    return authFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }, [authFetch]);

  /**
   * PUT request
   */
  const put = useCallback(async (endpoint, body = {}) => {
    return authFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }, [authFetch]);

  /**
   * PATCH request
   */
  const patch = useCallback(async (endpoint, body = {}) => {
    return authFetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }, [authFetch]);

  /**
   * DELETE request
   */
  const del = useCallback(async (endpoint, body = null) => {
    const options = { method: 'DELETE' };
    if (body) {
      options.body = JSON.stringify(body);
    }
    return authFetch(endpoint, options);
  }, [authFetch]);

  return {
    get,
    post,
    put,
    patch,
    delete: del,
  };
}

export default useApi;
